import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { SingleDatePicker } from 'react-dates';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import Loading from '../../components/Loading/Loading';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';

const READ_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ReadingCOR', label: 'Customer' },
  { value: 'ReadingOverride', label: 'Override' },
];

const ReadingsGrid = () => {
  const [readType, setReadType] = useState('');
  const [mpan, setMpan] = useState('');
  const [debouncedMpan, setDebouncedMpan] = useState('');
  const [receivedDate, setReceivedDate] = useState(null);
  const [dateFocused, setDateFocused] = useState(false);

  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (readType) {
        params.append('read_type', readType);
      }
      if (debouncedMpan) {
        params.append('mpan', debouncedMpan);
      }
      if (receivedDate) {
        params.append('received_date', receivedDate.toISOString());
      }
      params.append('page', page - 1);
      params.append('size', sizePerPage);
      if (sortBy) {
        params.append('sortBy', sortBy);
      }
      if (sortDir) {
        params.append('sortDir', sortDir);
      }

      const response = await axios.get(`/df/api/reading?${params.toString()}`, {
        headers: { Authorization: 'Bearer ' + localStorage.token },
      });

      setData(response.data.content || []);
      setTotalSize((response.data.page && response.data.page.totalElements) || 0);
    } catch (err) {
      setData([]);
      setTotalSize(0);
    } finally {
      setLoading(false);
    }
  }, [readType, debouncedMpan, receivedDate, page, sizePerPage, sortBy, sortDir]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMpan(mpan);
    }, 500);
    return () => clearTimeout(timer);
  }, [mpan]);

  useEffect(() => {
    fetchData();
  }, [page, sizePerPage, sortBy, sortDir, readType, receivedDate, debouncedMpan]);

  const formatContent = (cell) => {
    if (!cell) return '-';
    try {
      return (
        <pre
          style={{
            whiteSpace: 'pre',
            overflowX: 'auto',
            maxHeight: '200px',
            textAlign: 'left',
            fontSize: '12px',
          }}
        >
          {JSON.stringify(cell, null, 2)}
        </pre>
      );
    } catch (err) {
      return String(cell);
    }
  };

  const columns = [
    {
      dataField: 'sender',
      text: 'Sender',
      sort: true,
    },
    {
      dataField: 'senderRole',
      text: 'Sender Role',
      sort: true,
    },
    {
      dataField: 'receiver',
      text: 'Receiver',
      sort: true,
    },
    {
      dataField: 'receiverRole',
      text: 'Receiver Role',
      sort: true,
    },
    {
      dataField: 'mpan',
      text: 'MPAN',
      sort: true,
    },
    {
      dataField: 'meterRegisterId',
      text: 'Meter Register ID',
      sort: true,
    },
    {
      dataField: 'readingTimestamp',
      text: 'Version Date / Received Date',
      sort: true,
      formatter: (cell) => (cell ? moment(cell).format('DD/MM/YYYY') : ''),
    },
    {
      dataField: 'readType',
      text: 'Reading Type',
      sort: true,
      formatter: (cell) => {
        const option = READ_TYPE_OPTIONS.find((opt) => opt.value === cell);
        return option ? option.label : cell;
      },
    },
    {
      dataField: 'content',
      text: 'Content',
      sort: false,
      formatter: formatContent,
    },
  ];

  return (
    <div className='readings-grid'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Readings</h1>
        </Col>
      </Row>
      <div className='page-cnt'>
        <div className='table-filters-cnt mb-4'>
          <Row className='mb-3 align-items-end'>
            <Col xs='12' sm='3'>
              <FormGroup>
                <Label>Reading Type:</Label>
                <Input type='select' value={readType} onChange={(e) => setReadType(e.target.value)}>
                  {READ_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs='12' sm='3'>
              <FormGroup>
                <Label>MPAN:</Label>
                <Input type='text' value={mpan} onChange={(e) => setMpan(e.target.value)} placeholder='Enter MPAN' />
              </FormGroup>
            </Col>
            <Col xs='12' sm='3'>
              <FormGroup>
                <Label className='d-block'>Version Date / Received Date:</Label>
                <SingleDatePicker
                  date={receivedDate}
                  onDateChange={(date) => setReceivedDate(date)}
                  displayFormat={'DD/MM/YYYY'}
                  focused={dateFocused}
                  onFocusChange={({ focused }) => setDateFocused(focused)}
                  numberOfMonths={1}
                  isOutsideRange={() => false}
                  showClearDate={true}
                  id='received-date-picker'
                  placeholder='Select Date'
                />
              </FormGroup>
            </Col>
          </Row>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <BootstrapTable
            keyField='mpan'
            classes={'text-center align-middle'}
            headerClasses='th-expand'
            wrapperClasses='table-responsive'
            noDataIndication={() => <div>No records found.</div>}
            columns={columns}
            data={data}
            bordered={false}
            hover
            remote={{
              filter: true,
              pagination: true,
              sort: true,
              cellEdit: false,
            }}
            sort={{
              dataField: sortBy,
              order: sortDir,
            }}
            pagination={paginationFactory({
              page: page,
              sizePerPage: sizePerPage,
              sizePerPageList: [
                { text: '10', value: 10 },
                { text: '20', value: 20 },
                { text: '50', value: 50 },
                { text: '100', value: 100 },
              ],
              showTotal: true,
              totalSize: totalSize,
            })}
            onTableChange={(type, newState) => {
              if (type === 'pagination') {
                setPage(newState.page);
                setSizePerPage(newState.sizePerPage);
              }
              if (type === 'sort') {
                setSortBy(newState.sortField);
                setSortDir(newState.sortOrder);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ReadingsGrid;
